import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Text } from 'components/Text';
import { SizeEnum } from 'utils';
import { Button } from 'components/Button';
import { useMutation, useQuery } from '@apollo/client';
import { useUser } from 'context/user-context';
import { AccountImageUpload } from 'components/AccountImageUpload';
import { UPDATE_USER_INFO } from 'gql/mutations';
import { GET_USER_MFA_STATUS } from 'gql/queries';
import { Modal } from 'components/Modal';
import { NotificationTypes, apiErrorHandler } from 'utils';
import { withNotification } from 'components/Notification';
import { useSignOut, useConfigMfa, useNhostClient } from '@nhost/react';
import SettingsViewWrapper from 'components/SettingsWrapperView';

export const Account = withNotification(({ showNotification }: any) => {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);

  const showResetPassoword = searchParams.get('password');

  const { user, changeEmail, changePassword } = useUser();

  const [userInfo, setUserInfo] = useState({
    name: user?.displayName || '',
    email: user?.email || '',
  });

  useEffect(() => {
    if (showResetPassoword) setChangePaswordModal(true);
  }, [showResetPassoword]);

  const [userPassword, setUserPassword] = useState({
    newPassword: '',
    confirmPassword: '',
  });

  const [showModal, setShowModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [changePaswordModal, setChangePaswordModal] = useState(false);
  const [updateAvatar, { error, loading: avatarLoading }] = useMutation(
    UPDATE_USER_INFO,
    {
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
    },
  );
  const [updateName, { error: nameError, loading: nameLoading }] = useMutation(
    UPDATE_USER_INFO,
    {
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
    },
  );
  const { signOut } = useSignOut(true);

  const nhost = useNhostClient();

  const { data: mfaStatusData, refetch: refetchMfaStatus } = useQuery(
    GET_USER_MFA_STATUS,
    {
      variables: { id: user?.id },
      skip: !user?.id,
      fetchPolicy: 'network-only',
    },
  );
  const isMfaEnabled = mfaStatusData?.user?.activeMfaType === 'totp';

  const [showMfaModal, setShowMfaModal] = useState(false);
  const [mfaActivationCode, setMfaActivationCode] = useState('');
  const {
    generateQrCode,
    isGenerating: isGeneratingMfaQrCode,
    qrCodeDataUrl,
    isGenerated: isMfaQrCodeGenerated,
    activateMfa,
    isActivating: isActivatingMfa,
    isActivated: isMfaActivated,
    isError: isMfaError,
    error: mfaError,
  } = useConfigMfa();

  const handleOpenMfaModal = () => {
    setMfaActivationCode('');
    setShowMfaModal(true);
    generateQrCode();
  };

  useEffect(() => {
    if (isMfaActivated) {
      showNotification(
        NotificationTypes.success,
        'Two-factor authentication enabled',
      );
      setShowMfaModal(false);
      refetchMfaStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMfaActivated]);

  // hasura-auth doesn't expose a disable path through useConfigMfa (enable-only), so this calls
  // its REST endpoint directly — same one activation uses, just with an empty activeMfaType.
  // Requires a current code, same as activating: proves the person disabling it still has the
  // device, not just a valid session (otherwise a stolen session cookie alone could turn off MFA).
  const [showDisableMfaModal, setShowDisableMfaModal] = useState(false);
  const [mfaDisableCode, setMfaDisableCode] = useState('');
  const [isDisablingMfa, setIsDisablingMfa] = useState(false);
  const [disableMfaError, setDisableMfaError] = useState('');

  const handleOpenDisableMfaModal = () => {
    setMfaDisableCode('');
    setDisableMfaError('');
    setShowDisableMfaModal(true);
  };

  const handleDisableMfa = async () => {
    setIsDisablingMfa(true);
    setDisableMfaError('');
    try {
      // hasura-auth-js exposes no public API to disable MFA (useConfigMfa is enable-only) or to
      // read back the auth service's own base URL — HasuraAuthClient's backendUrl exists at
      // runtime but isn't part of its public .d.ts, so reading it off the client instance would
      // mean depending on an undocumented internal that could change without notice. Using an
      // explicit env var instead keeps this on the same public contract every other Nhost env var
      // in this app already relies on.
      if (!process.env.REACT_APP_NHOST_AUTH_URL) {
        throw new Error('REACT_APP_NHOST_AUTH_URL is not configured');
      }
      const response = await fetch(
        `${process.env.REACT_APP_NHOST_AUTH_URL}/user/mfa`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${nhost.auth.getAccessToken()}`,
          },
          body: JSON.stringify({ code: mfaDisableCode, activeMfaType: '' }),
        },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(
          body?.message || 'Could not disable two-factor authentication',
        );
      }
      showNotification(
        NotificationTypes.success,
        'Two-factor authentication disabled',
      );
      setShowDisableMfaModal(false);
      refetchMfaStatus();
    } catch (err: any) {
      setDisableMfaError(
        err?.message || 'Could not disable two-factor authentication',
      );
    } finally {
      setIsDisablingMfa(false);
    }
  };

  const handleUserAvatarUpdate = (imgUrl: string) => {
    updateAvatar({
      variables: { id: user?.id, userObj: { avatarUrl: imgUrl } },
    })
      .then((data) => {
        setShowModal(false);
        return showNotification(
          NotificationTypes.success,
          'User profile photo updated',
        );
      })
      .catch((err) => {
        return showNotification(
          NotificationTypes.error,
          error ? error.message : 'Could not update profile photo',
        );
      });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;
    setUserInfo({ ...userInfo, [name]: value });
  };

  const handlePasswordFieldChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value, name } = event.target;
    setUserPassword({ ...userPassword, [name]: value });
  };

  const handleUpdate = () => {
    updateName({
      variables: { id: user?.id, userObj: { displayName: userInfo.name } },
    })
      .then((data) => {
        setShowModal(false);
        return showNotification(NotificationTypes.success, 'User data updated');
      })
      .catch((err) => {
        return showNotification(
          NotificationTypes.error,
          nameError ? nameError.message : 'Could not update name',
        );
      });
  };

  const handleUpdateEmail = async () => {
    if (!changeEmail) return;
    const email = userInfo.email.trim();
    if (!email) {
      showNotification(NotificationTypes.error, 'Enter an email address.');
      return;
    }
    try {
      const result = await changeEmail.changeEmail(email, {
        redirectTo: '/email-change',
      });
      if (result.isError || !result.needsEmailVerification) {
        showNotification(
          NotificationTypes.error,
          result.error?.message ||
            'Could not send the email confirmation. Please try again.',
        );
        return;
      }
      setShowEmailModal(false);
      showNotification(
        NotificationTypes.success,
        `Check ${email} for a link to confirm your new email address.`,
      );
    } catch {
      showNotification(
        NotificationTypes.error,
        'Could not send the email confirmation. Please try again.',
      );
    }
  };

  const handleChangePassword = () => {
    if (userPassword.newPassword !== userPassword.confirmPassword) {
      setUserPassword({
        newPassword: '',
        confirmPassword: '',
      });
      return showNotification(
        NotificationTypes.pending,
        'Passwords dont match! re-enter passwords',
      );
    }

    changePassword
      ?.changePassword(userPassword.newPassword)
      .then((data) => {
        setUserPassword({
          newPassword: '',
          confirmPassword: '',
        });

        searchParams.delete('password');
        navigate('/settings/account');

        setChangePaswordModal(false);
        return showNotification(
          NotificationTypes.success,
          'Password changed successfully',
        );
      })
      .catch((err) => {
        return showNotification(
          NotificationTypes.error,
          'Password reset failed',
        );
      });
  };

  return (
    <SettingsViewWrapper activeNav="account">
      <div>
        <Modal
          openModal={(value) => setShowModal(value)}
          open={showModal}
          containerClass="sm:max-w-lg w-full sm:w-auto p-5"
        >
          <Text value="Update name" type="h2" />
          <div className="mt-4">
            <input
              type="name"
              name="name"
              id="name"
              className="border p-2 sm:w-64 focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="enter user's name e.g. Isaac John"
              value={userInfo.name}
              onChange={handleChange}
            />
          </div>
          <Button
            onClick={handleUpdate}
            size={SizeEnum.md}
            className="bg-blue-600 text-white hover:bg-blue-700 h-max px-6 mt-4 sm:w-64 w-full justify-center"
            isLoading={nameLoading}
          >
            Update name
          </Button>
        </Modal>
        <Modal
          openModal={(value) => setShowEmailModal(value)}
          open={showEmailModal}
          containerClass="sm:max-w-lg w-full sm:w-auto p-5"
        >
          <Text value="Update Email" type="h2" />
          <div className="mt-4">
            <input
              type="email"
              name="email"
              id="email"
              className="border p-2 sm:w-64 focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="enter user's email e.g.you@example.com"
              value={userInfo.email}
              onChange={handleChange}
            />
          </div>
          <Button
            onClick={() => handleUpdateEmail()}
            size={SizeEnum.md}
            className="bg-blue-600 text-white hover:bg-blue-700 h-max px-6 mt-4 sm:w-64 w-full justify-center"
            isLoading={changeEmail?.isLoading}
          >
            Update Email
          </Button>
        </Modal>
        <Modal
          openModal={(value) => setChangePaswordModal(value)}
          open={changePaswordModal}
          containerClass="sm:max-w-lg w-full sm:w-auto p-5"
        >
          {/* New Password */}
          <Text value="New Password" type="h4" />
          <div className="mt-4 mb-4">
            <input
              type="password"
              name="newPassword"
              id="newPassword"
              className="border p-2 sm:w-64 focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="Enter your new password"
              value={userPassword.newPassword}
              onChange={handlePasswordFieldChange}
            />
          </div>

          {/* Confirm Password */}
          <Text value="Confirm Password" type="h4" />
          <div className="mt-4">
            <input
              type="password"
              name="confirmPassword"
              id="confirmPassword"
              className="border p-2 sm:w-64 focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="Confirm your new password"
              value={userPassword.confirmPassword}
              onChange={handlePasswordFieldChange}
            />
          </div>

          <Button
            onClick={() => handleChangePassword()}
            size={SizeEnum.md}
            className="bg-blue-600 text-white hover:bg-blue-700 h-max px-6 mt-4 sm:w-64 w-full justify-center"
            isLoading={changeEmail?.isLoading}
          >
            Update Password
          </Button>
        </Modal>
        <Modal
          openModal={(value) => setShowMfaModal(value)}
          open={showMfaModal}
          containerClass="sm:max-w-lg w-full sm:w-auto p-5"
        >
          <Text value="Enable two-factor authentication" type="h2" />
          <div className="mt-4">
            <Text
              value="Scan this QR code with an authenticator app (e.g. Google Authenticator, Authy), then enter the 6-digit code it generates below."
              type="sm"
              className="text-gray-500"
            />
          </div>
          <div className="mt-4 flex justify-center">
            {isGeneratingMfaQrCode && (
              <Text value="Generating QR code..." type="sm" />
            )}
            {isMfaQrCodeGenerated && qrCodeDataUrl && (
              <img
                src={qrCodeDataUrl}
                alt="MFA QR code"
                className="h-40 w-40"
              />
            )}
          </div>
          <div className="mt-4">
            <input
              type="text"
              inputMode="numeric"
              name="mfaActivationCode"
              id="mfaActivationCode"
              className="border p-2 sm:w-64 focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="Enter the 6-digit code"
              value={mfaActivationCode}
              onChange={(e) => setMfaActivationCode(e.target.value)}
            />
          </div>
          {isMfaError && (
            <Text
              value={
                mfaError?.message ||
                'Could not activate two-factor authentication'
              }
              type="sm"
              className="text-red-600 mt-2"
            />
          )}
          <Button
            onClick={() => activateMfa(mfaActivationCode)}
            size={SizeEnum.md}
            className="bg-blue-600 text-white hover:bg-blue-700 h-max px-6 mt-4 sm:w-64 w-full justify-center"
            isLoading={isActivatingMfa}
            disabled={!isMfaQrCodeGenerated || mfaActivationCode.length !== 6}
          >
            Activate
          </Button>
        </Modal>
        <Modal
          openModal={(value) => setShowDisableMfaModal(value)}
          open={showDisableMfaModal}
          containerClass="sm:max-w-lg w-full sm:w-auto p-5"
        >
          <Text value="Disable two-factor authentication" type="h2" />
          <div className="mt-4">
            <Text
              value="Enter the current 6-digit code from your authenticator app to confirm."
              type="sm"
              className="text-gray-500"
            />
          </div>
          <div className="mt-4">
            <input
              type="text"
              inputMode="numeric"
              name="mfaDisableCode"
              id="mfaDisableCode"
              className="border p-2 sm:w-64 focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="Enter the 6-digit code"
              value={mfaDisableCode}
              onChange={(e) => setMfaDisableCode(e.target.value)}
            />
          </div>
          {disableMfaError && (
            <Text
              value={disableMfaError}
              type="sm"
              className="text-red-600 mt-2"
            />
          )}
          <Button
            onClick={handleDisableMfa}
            size={SizeEnum.md}
            className="bg-red-500 text-white hover:bg-red-700 h-max px-6 mt-4 sm:w-64 w-full justify-center"
            isLoading={isDisablingMfa}
            disabled={mfaDisableCode.length !== 6}
          >
            Disable
          </Button>
        </Modal>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 pt-6">
          <h1 className="text-[28px] font-semibold text-[#202223] leading-tight">
            Account
          </h1>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 mt-3">
          <AccountImageUpload
            onUpload={({ urls }) => handleUserAvatarUpdate(urls[0])}
            defaultImage={user?.avatarUrl}
            isLoading={avatarLoading}
          />
          <div className=" border-b" />
          <div className="flex w-full sm:w-1/2 justify-between py-4 mt-3">
            <div>
              <Text
                value="Name"
                type="body"
                className="text-gray-600 font-bold"
              />
              <Text
                value={userInfo.name || ''}
                type="sm"
                className="text-gray-500"
              />
            </div>
            <Button
              onClick={() => setShowModal(true)}
              size={SizeEnum.md}
              className="h-max px-6"
            >
              Edit
            </Button>
          </div>
          <div className="flex w-full sm:w-1/2 justify-between py-4 mb-3">
            <div>
              <Text
                value="Email address"
                type="body"
                className="text-gray-600 font-bold"
              />
              <Text
                value={user?.email || ''}
                type="sm"
                className="text-gray-500"
              />
            </div>
            <Button
              onClick={() => setShowEmailModal(true)}
              size={SizeEnum.md}
              className="h-max px-6"
            >
              Edit
            </Button>
          </div>
          <div className=" border-b" />
          <div className="flex w-full sm:w-1/2 justify-between py-4 mt-3">
            <div>
              <Text value="Login" type="h4" className="text-gray-600" />
              <Text
                value="Password"
                type="body"
                className="text-gray-600 font-bold"
              />
              <Text
                value="Password last updated: November 17"
                type="sm"
                className="text-gray-500"
              />
            </div>
            <Button
              onClick={() => setChangePaswordModal(true)}
              size={SizeEnum.md}
              className="h-max px-6"
              isLoading={changePassword?.isLoading}
            >
              Change Password
            </Button>
          </div>
          <div className="flex w-full sm:w-1/2 justify-between py-4">
            <div>
              <Text value="Security" type="h4" className="text-gray-600" />
              <Text
                value="Two-factor authentication"
                type="body"
                className="text-gray-600 font-bold"
              />
              {isMfaEnabled ? (
                <Text
                  value="✓ Enabled — a code from your authenticator app is required at login"
                  type="sm"
                  className="text-green-600"
                />
              ) : (
                <Text
                  value="After entering your password, you will be asked for a second verification step from an authenticator app"
                  type="sm"
                  className="text-gray-500"
                />
              )}
            </div>
            {isMfaEnabled ? (
              <Button
                onClick={handleOpenDisableMfaModal}
                size={SizeEnum.md}
                className="h-max px-6"
              >
                Disable
              </Button>
            ) : (
              <Button
                onClick={handleOpenMfaModal}
                size={SizeEnum.md}
                className="h-max px-6"
              >
                Enable
              </Button>
            )}
          </div>
          <div className=" w-full sm:w-1/2 justify-between py-4">
            <div>
              <Text
                value="Sign out from all devices"
                type="body"
                className="text-gray-600 font-bold"
              />
              <Text
                value="Logged in on a shared device but forgot to sign out? End all sessions by signing out from all devices"
                type="sm"
                className="text-gray-500"
              />
            </div>
            <Button
              onClick={() => signOut(true)}
              size={SizeEnum.md}
              className="h-max px-6 mt-3"
            >
              Sign out from all your devices
            </Button>
          </div>
          <div className=" w-full sm:w-1/2 justify-between py-4">
            <div>
              <Text
                value="Delete your account"
                type="body"
                className="text-gray-600 font-bold"
              />
              <Text
                value="By deleting your account, you will no longer be able to access any of your orders or claims"
                type="sm"
                className="text-gray-500"
              />
            </div>
            <Button
              onClick={() => null}
              size={SizeEnum.md}
              className="bg-red-500 text-white hover:bg-red-700 h-max px-6 mt-3"
            >
              Delete your account
            </Button>
          </div>
        </div>
      </div>
    </SettingsViewWrapper>
  );
});
